import React, { useState, useEffect } from 'react';
import '../../../scss/_mobiledraw.scss';
import { MobileDrawHeader } from './MobileDrawHeader';
import { applicationInsights } from '../../../../ApplicationInsights';
import { DrawApi } from '@apply/api';
import { IDraw, IDrawBudget, IDrawBudgetItem, IDrawItem, IPostSaveDrawItem } from '@apply/api/src/api/DrawApi';
import { MobileDrawDetails } from './MobileDrawDetails/MobileDrawDetails';
import { DrawBudgetLineCategory } from '@apply/constants';
import { Button, ButtonTheme } from '@apply/components';
import { useNavigate } from 'react-router-dom';
import { ConfirmV2 } from '@apply/ui';

export interface MobileDrawItem extends IDrawItem {
    drawBudgetItemId: number;
    dirty: boolean;
    categoryId: DrawBudgetLineCategory;
    description: string;
    budgetAmount: number;
    equityAmount: number;
    acquisitionAmount: number;
    availableAmount: number;
    drawHistory: number[];
}

export const MobileDrawControl: React.FC = () => {
    const navigate = useNavigate();

    const drawBudgetId = parseInt(location.pathname.split('/')[2]);
    const drawApi = new DrawApi.DrawApi(applicationInsights);

    const [drawBudget, set_drawBudget] = useState<IDrawBudget>();
    const [mobileDrawItems, set_mobileDrawItems] = useState<MobileDrawItem[]>([]);
    const [hideFullyFunded, set_hideFullyFunded] = useState(true);
    const [totalDrawAmount, set_totalDrawAmount] = useState<number>(0);
    const [openConfirmDelete, set_openConfirmDelete] = useState(false);

    useEffect(() => {
        const total = mobileDrawItems?.reduce((sum, value) => (sum += value?.borrowerRequest ?? 0), 0);

        set_totalDrawAmount(total);
    }, [mobileDrawItems]);

    const loadData = async () => {
        const response = await drawApi.getDrawBudget(drawBudgetId);
        if (response.statusCode !== 200) return;

        const budgetResult = response.result;
        set_drawBudget(budgetResult);

        loadMobileDrawItems(budgetResult);
    };

    function loadMobileDrawItems(budget: IDrawBudget | undefined) {
        if (!budget) return;

        const latestDraw = budget.draws[budget.draws?.length - 1];

        const items: MobileDrawItem[] = [];
        budget.drawBudgetItems.map((budgetItem) => {
            const drawItem = budgetItem.drawItems?.find((drawItem) => drawItem.drawNumber === budget.drawCount - 1);

            let item: MobileDrawItem;
            const baseData = newMobileDrawItem(budget, budgetItem, latestDraw);
            if (drawItem) {
                item = { ...baseData, ...drawItem };
            } else {
                item = baseData;
            }

            items.push(item);
        });

        set_mobileDrawItems(items);
    }

    let newItemIndex = -1;
    const newMobileDrawItem = (budget: IDrawBudget, budgetItem: IDrawBudgetItem, latestDraw: IDraw): MobileDrawItem => {
        if (!latestDraw) throw new Error('latestDraw required');

        return {
            id: newItemIndex--,
            drawBudgetItemId: budgetItem.id,
            borrowerRequest: 0,
            drawId: latestDraw.id,
            drawNumber: latestDraw.drawNumber,
            funded: 0,
            inspection: 0,
            notes: '',
            dirty: false,
            categoryId: budgetItem.category,
            description: budgetItem.description,
            budgetAmount: budgetItem.budgetAmount,
            equityAmount: budgetItem.equityAmount,
            acquisitionAmount: budgetItem.acquisitionAmount,
            availableAmount: calculateAvailable(budgetItem),
            drawHistory: generateDrawHistory(budget, budgetItem),
        };
    };

    const calculateAvailable = (drawBudgetItem: IDrawBudgetItem) => {
        if (!drawBudgetItem) return 0;

        const fundedLineTotal = drawBudgetItem.drawItems?.reduce((sum, value) => (sum += value?.funded ?? 0), 0) ?? 0;

        const amountDrawn = drawBudgetItem.acquisitionAmount + fundedLineTotal;

        return Math.round((drawBudgetItem.budgetAmount - drawBudgetItem.equityAmount - amountDrawn) * 100) / 100;
    };

    const generateDrawHistory = (budget: IDrawBudget, drawBudgetItem: IDrawBudgetItem): number[] => {
        const history: number[] = [];

        //Do not show the current draw in draw history (-1 against drawCount)
        for (let i = 0; i < budget.drawCount - 1; i++) {
            const drawItem = drawBudgetItem.drawItems?.find((item) => item.drawNumber === i);

            if (drawItem) {
                history.push(drawItem.funded);
            } else {
                history.push(0);
            }
        }

        return history;
    };

    useEffect(() => {
        loadData();
    }, [drawBudgetId]);

    const set_mobileBorrowerRequest = (drawItemId: number, borrowerRequest: number) => {
        const updateItems = mobileDrawItems.map((item) => {
            if (item.id === drawItemId) {
                item.borrowerRequest = borrowerRequest;
                item.dirty = true;
            }

            return item;
        });

        set_mobileDrawItems(updateItems);
    };

    const saveMobileDraw = async () => {
        const itemsToSave: IPostSaveDrawItem[] = mobileDrawItems
            .filter((item) => item.dirty)
            .map((item) => {
                return {
                    drawBudgetItemId: item.drawBudgetItemId,
                    drawId: item.drawId,
                    drawNumber: item.drawNumber,
                    value: item.borrowerRequest,
                };
            });

        if (itemsToSave.length === 0) return;

        await drawApi.drawItemBorrowerRequest_v2_mobile(itemsToSave);
    };

    const onReviewAndSubmitClick = async () => {
        await saveMobileDraw();
        navigate(`/mobilereviewandsubmit/${drawBudgetId}`);
    };

    const handleConfirmDelete = async () => {
        if (!drawBudget) return;
        const latestDraw = drawBudget.draws[drawBudget.draws?.length - 1];
        if (!latestDraw) throw new Error('latestDraw required');

        const response = await drawApi.deleteDraw(latestDraw.id);

        if (response.statusCode !== 200) {
            alert(response.errors?.[0] || response.statusCode);
            return;
        }

        if (!response.result) return;

        navigate('/home');
    };

    const onCancelDrawClick = async () => {
        set_openConfirmDelete(true);
    };

    const handleCancelDelete = async () => {
        set_openConfirmDelete(false);
    };

    const cancelDrawDisabled = () => {
        if (!drawBudget) return;
        const latestDraw = drawBudget.draws[drawBudget.draws?.length - 1];
        if (!latestDraw) throw new Error('latestDraw required');

        if (latestDraw.isFinalized || latestDraw.inspectionOrdered) {
            return true;
        } else {
            return false;
        }
    };

    return (
        <main style={{ padding: 0 }}>
            <MobileDrawHeader
                drawBudget={drawBudget}
                mobileDrawItems={mobileDrawItems}
                hideFullyFunded={hideFullyFunded}
                onToggleFullyFunded={set_hideFullyFunded}
                saveMobileDraw={saveMobileDraw}
            />
            <MobileDrawDetails
                mobileDrawItems={mobileDrawItems}
                hideFullyFunded={hideFullyFunded}
                set_mobileBorrowerRequest={set_mobileBorrowerRequest}
            />
            <Button
                id='saveFinishButton'
                theme={ButtonTheme.Primary}
                width={'80%'}
                handleClick={onReviewAndSubmitClick}
                disabled={totalDrawAmount <= 0}
            >
                Review and Submit
            </Button>
            <Button
                id='cancelDrawButton'
                theme={ButtonTheme.LinkRed}
                width={'80%'}
                handleClick={onCancelDrawClick}
                disabled={cancelDrawDisabled()}
            >
                Cancel Draw
            </Button>
            <ConfirmV2
                id='confirmCancelDraw'
                open={openConfirmDelete}
                title='Cancel Draw'
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
                okLabel='Yes'
                cancelLabel='No'
                width='340px'
                height='200px'
            >
                Are you sure you wish to delete this draw? (Draw #{drawBudget?.drawCount})
            </ConfirmV2>
        </main>
    );
};
